import { useEffect, useState } from "react";

import { format } from "date-fns";
import { Loader2, Shield, Ban, TrendingDown, AlertTriangle, ArrowLeft, Unlock, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from "recharts";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";


interface IPReputation {
  ip_address: string;
  reputation_score: number;
  total_violations: number;
  total_successful_logins: number;
  last_violation_at: string | null;
  last_successful_login_at: string | null;
  created_at: string;
  updated_at: string;
  as_name?: string | null;
  as_number?: string | null;
  reverse_dns?: string | null;
}

interface BlockedIP {
  id: string;
  ip_address: string;
  blocked_at: string;
  unblock_at: string;
  block_reason: string;
  violation_count: number;
  is_manual: boolean;
  notes: string | null;
}

interface Stats {
  totalIPs: number;
  blockedIPs: number;
  lowReputationIPs: number;
  avgReputation: number;
}

interface ASNStats {
  as_number: string;
  as_name: string;
  ip_count: number;
  avg_reputation: number;
  threat_count: number;
}

const AdminIPManagement = () => {
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  
  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate("/dashboard"),
  });
  
  const [ipReputations, setIPReputations] = useState<IPReputation[]>([]);
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalIPs: 0,
    blockedIPs: 0,
    lowReputationIPs: 0,
    avgReputation: 0,
  });
  const [asnStats, setAsnStats] = useState<ASNStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedASN, setSelectedASN] = useState<string>("");
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedIP, setSelectedIP] = useState<IPReputation | null>(null);
  const [newReputation, setNewReputation] = useState<number>(100);
  const [unblockDialogOpen, setUnblockDialogOpen] = useState(false);
  const [selectedBlockedIP, setSelectedBlockedIP] = useState<BlockedIP | null>(null);
  const [manualBlockDialogOpen, setManualBlockDialogOpen] = useState(false);
  const [manualBlockIP, setManualBlockIP] = useState("");
  const [manualBlockReason, setManualBlockReason] = useState("");
  const [manualBlockNotes, setManualBlockNotes] = useState("");
  const [decayLoading, setDecayLoading] = useState(false);

  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate("/");
      return;
    }

    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, roleLoading, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch IP reputations with AS and rDNS data
      const { data: reputationData, error: reputationError } = await supabase
        .from("ip_reputation")
        .select("*")
        .order("reputation_score", { ascending: true });

      if (reputationError) throw reputationError;

      // Enrich with AS and rDNS data from ip_reputation_cache and admin_email_access_logs
      const enrichedReputations = await Promise.all(
        (reputationData || []).map(async (rep) => {
          // First try ip_reputation_cache which has enrichment data
          const { data: cacheData } = await supabase
            .from("ip_reputation_cache")
            .select("geo_data")
            .eq("ip_address", rep.ip_address)
            .single();

          // Extract AS and rDNS from geo_data if available
          const geoData = cacheData?.geo_data as any;
          let asName = geoData?.asname;
          let asNumber = geoData?.as;
          let reverseDns = geoData?.reverse;

          // If not found in cache, try admin_email_access_logs
          if (!asName && !asNumber && !reverseDns) {
            const { data: logData } = await supabase
              .from("admin_email_access_logs")
              .select("as_name, as_number, reverse_dns")
              .eq("ip_address", rep.ip_address)
              .order("created_at", { ascending: false })
              .limit(1)
              .single();

            asName = logData?.as_name;
            asNumber = logData?.as_number;
            reverseDns = logData?.reverse_dns;
          }

          return {
            ...rep,
            as_name: asName,
            as_number: asNumber,
            reverse_dns: reverseDns,
          };
        })
      );

      setIPReputations(enrichedReputations);

      // Calculate ASN statistics
      const asnMap = new Map<string, { 
        as_name: string; 
        ip_count: number; 
        total_reputation: number;
        threat_count: number;
      }>();

      enrichedReputations.forEach((rep) => {
        if (rep.as_number) {
          const key = rep.as_number;
          const existing = asnMap.get(key) || {
            as_name: rep.as_name || "Unknown",
            ip_count: 0,
            total_reputation: 0,
            threat_count: 0,
          };

          existing.ip_count++;
          existing.total_reputation += rep.reputation_score;
          if (rep.reputation_score < 50) existing.threat_count++;

          asnMap.set(key, existing);
        }
      });

      const asnStatsData: ASNStats[] = Array.from(asnMap.entries())
        .map(([as_number, data]) => ({
          as_number,
          as_name: data.as_name,
          ip_count: data.ip_count,
          avg_reputation: Math.round(data.total_reputation / data.ip_count),
          threat_count: data.threat_count,
        }))
        .sort((a, b) => b.ip_count - a.ip_count);

      setAsnStats(asnStatsData);

      // Fetch blocked IPs
      const { data: blockedData, error: blockedError } = await supabase
        .from("blocked_ips")
        .select("*")
        .order("blocked_at", { ascending: false });

      if (blockedError) throw blockedError;

      setBlockedIPs(blockedData || []);

      // Calculate stats
      const totalIPs = reputationData?.length || 0;
      const blockedIPs = blockedData?.length || 0;
      const lowReputationIPs = reputationData?.filter((ip) => ip.reputation_score < 50).length || 0;
      const avgReputation = totalIPs > 0 
        ? reputationData.reduce((sum, ip) => sum + ip.reputation_score, 0) / totalIPs 
        : 100;

      setStats({
        totalIPs,
        blockedIPs,
        lowReputationIPs,
        avgReputation,
      });
    } catch (error) {
      console.error("Error fetching IP management data:", error);
      toast.error("Failed to load IP management data");
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustReputation = async () => {
    if (!selectedIP) return;

    try {
      const { error } = await supabase
        .from("ip_reputation")
        .update({ reputation_score: newReputation, updated_at: new Date().toISOString() })
        .eq("ip_address", selectedIP.ip_address);

      if (error) throw error;

      toast.success(`Reputation adjusted to ${newReputation} for ${selectedIP.ip_address}`);
      setAdjustDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error adjusting reputation:", error);
      toast.error("Failed to adjust reputation");
    }
  };

  const handleUnblockIP = async () => {
    if (!selectedBlockedIP) return;

    try {
      const { error } = await supabase
        .from("blocked_ips")
        .delete()
        .eq("id", selectedBlockedIP.id);

      if (error) throw error;

      toast.success(`Unblocked ${selectedBlockedIP.ip_address}`);
      setUnblockDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error unblocking IP:", error);
      toast.error("Failed to unblock IP");
    }
  };

  const handleManualBlock = async () => {
    if (!manualBlockIP.trim() || !manualBlockReason.trim()) {
      toast.error("IP address and reason are required");
      return;
    }

    try {
      const { error } = await supabase
        .from("blocked_ips")
        .insert({
          ip_address: manualBlockIP.trim(),
          block_reason: manualBlockReason.trim(),
          notes: manualBlockNotes.trim() || null,
          is_manual: true,
          violation_count: 0,
          unblock_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days
        });

      if (error) throw error;

      toast.success(`Manually blocked ${manualBlockIP}`);
      setManualBlockDialogOpen(false);
      setManualBlockIP("");
      setManualBlockReason("");
      setManualBlockNotes("");
      fetchData();
    } catch (error) {
      console.error("Error manually blocking IP:", error);
      toast.error("Failed to block IP");
    }
  };

  const handleRunDecay = async () => {
    try {
      setDecayLoading(true);
      const { data, error } = await supabase.rpc("run_batch_reputation_decay");

      if (error) throw error;

      const result = data as any;
      toast.success(`Decay applied to ${result.total_decayed} IPs out of ${result.total_processed} processed`);
      fetchData();
    } catch (error) {
      console.error("Error running decay:", error);
      toast.error("Failed to run reputation decay");
    } finally {
      setDecayLoading(false);
    }
  };

  const getReputationColor = (score: number) => {
    if (score >= 100) return "text-green-600 dark:text-green-400";
    if (score >= 50) return "text-yellow-600 dark:text-yellow-400";
    if (score >= 0) return "text-orange-600 dark:text-orange-400";
    return "text-red-600 dark:text-red-400";
  };

  const getReputationBadge = (score: number) => {
    if (score >= 100) return <Badge variant="secondary" className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">Good</Badge>;
    if (score >= 50) return <Badge variant="secondary" className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">Fair</Badge>;
    if (score >= 0) return <Badge variant="secondary" className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300">Poor</Badge>;
    return <Badge variant="destructive">Critical</Badge>;
  };

  const filteredReputations = ipReputations.filter((ip) => {
    const matchesSearch = ip.ip_address.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesASN = !selectedASN || ip.as_number === selectedASN;
    return matchesSearch && matchesASN;
  });

  const filteredBlockedIPs = blockedIPs.filter((ip) =>
    ip.ip_address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Prepare chart data
  const reputationDistribution = [
    { range: "Critical (<0)", count: ipReputations.filter(ip => ip.reputation_score < 0).length },
    { range: "Poor (0-49)", count: ipReputations.filter(ip => ip.reputation_score >= 0 && ip.reputation_score < 50).length },
    { range: "Fair (50-99)", count: ipReputations.filter(ip => ip.reputation_score >= 50 && ip.reputation_score < 100).length },
    { range: "Good (100+)", count: ipReputations.filter(ip => ip.reputation_score >= 100).length },
  ];

  const topViolators = [...ipReputations]
    .sort((a, b) => b.total_violations - a.total_violations)
    .slice(0, 10)
    .map(ip => ({
      ip: ip.ip_address.slice(0, 15) + (ip.ip_address.length > 15 ? "..." : ""),
      violations: ip.total_violations,
      reputation: ip.reputation_score,
    }));

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">IP Reputation Management</h1>
              <p className="text-muted-foreground">Monitor and manage IP addresses, reputation scores, and blocked IPs</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRunDecay} variant="outline" disabled={decayLoading}>
              {decayLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Run Decay
            </Button>
            <Button onClick={fetchData} variant="outline">
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total IPs Tracked</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalIPs}</div>
              <p className="text-xs text-muted-foreground">IP addresses in database</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Blocked IPs</CardTitle>
              <Ban className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.blockedIPs}</div>
              <p className="text-xs text-muted-foreground">Currently blocked</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Low Reputation</CardTitle>
              <TrendingDown className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.lowReputationIPs}</div>
              <p className="text-xs text-muted-foreground">Score below 50</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Reputation</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.avgReputation.toFixed(1)}</div>
              <p className="text-xs text-muted-foreground">Across all tracked IPs</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid gap-6 lg:grid-cols-2 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>Reputation Distribution</CardTitle>
              <CardDescription>IP addresses by reputation range</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={reputationDistribution}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="range" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top Violators</CardTitle>
              <CardDescription>IPs with most violations</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topViolators} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis type="number" className="text-xs" />
                  <YAxis dataKey="ip" type="category" className="text-xs" width={100} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }} />
                  <Bar dataKey="violations" fill="hsl(var(--destructive))" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="reputation" className="space-y-4">
          <TabsList>
            <TabsTrigger value="reputation">IP Reputation</TabsTrigger>
            <TabsTrigger value="asn">ASN Statistics</TabsTrigger>
            <TabsTrigger value="blocked">Blocked IPs</TabsTrigger>
          </TabsList>

          <TabsContent value="asn" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Autonomous System (AS) Statistics</CardTitle>
                <CardDescription>Track IPs by their autonomous system number and network operator</CardDescription>
              </CardHeader>
              <CardContent>
                {asnStats.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No ASN data available yet
                  </p>
                ) : (
                  <div className="space-y-3">
                    {asnStats.map((asn) => (
                      <div
                        key={asn.as_number}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors cursor-pointer"
                        onClick={() => setSelectedASN(selectedASN === asn.as_number ? "" : asn.as_number)}
                      >
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{asn.as_name}</p>
                            <Badge variant="secondary" className="font-mono text-xs">
                              {asn.as_number}
                            </Badge>
                            {selectedASN === asn.as_number && (
                              <Badge variant="default">Filtered</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                            <span>IPs: <span className="font-semibold">{asn.ip_count}</span></span>
                            <span>Avg Reputation: <span className={getReputationColor(asn.avg_reputation) + " font-semibold"}>{asn.avg_reputation}</span></span>
                            <span>Threats: <span className="text-destructive font-semibold">{asn.threat_count}</span></span>
                            <span>Threat Rate: <span className="font-semibold">{((asn.threat_count / asn.ip_count) * 100).toFixed(1)}%</span></span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={selectedASN === asn.as_number ? "default" : "outline"}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedASN(selectedASN === asn.as_number ? "" : asn.as_number);
                          }}
                        >
                          {selectedASN === asn.as_number ? "Clear Filter" : "Filter IPs"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reputation" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle>IP Reputation Scores</CardTitle>
                    <CardDescription>
                      View and manage reputation scores for tracked IPs
                      {selectedASN && (
                        <Badge variant="secondary" className="ml-2">
                          Filtered by ASN: {selectedASN}
                        </Badge>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {selectedASN && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedASN("")}
                      >
                        Clear ASN Filter
                      </Button>
                    )}
                    <Input
                      placeholder="Search IP address..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-xs"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredReputations.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No IP reputation data found
                  </p>
                ) : (
                  <div className="space-y-3">
                    {filteredReputations.map((ip) => (
                      <div
                        key={ip.ip_address}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                      >
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-mono font-medium">{ip.ip_address}</p>
                            {getReputationBadge(ip.reputation_score)}
                            {ip.as_name && (
                              <Badge variant="outline" className="text-xs">
                                {ip.as_name}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                            <span>Score: <span className={getReputationColor(ip.reputation_score) + " font-semibold"}>{ip.reputation_score}</span></span>
                            <span>Violations: {ip.total_violations}</span>
                            <span>Successful Logins: {ip.total_successful_logins}</span>
                            <span>Updated: {format(new Date(ip.updated_at), "MMM d, h:mm a")}</span>
                          </div>
                          {(ip.as_number || ip.reverse_dns) && (
                            <div className="flex gap-4 text-xs text-muted-foreground pt-1">
                              {ip.as_number && (
                                <span className="font-mono">ASN: {ip.as_number}</span>
                              )}
                              {ip.reverse_dns && (
                                <span className="font-mono truncate">rDNS: {ip.reverse_dns}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedIP(ip);
                            setNewReputation(ip.reputation_score);
                            setAdjustDialogOpen(true);
                          }}
                        >
                          Adjust
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="blocked" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Blocked IP Addresses</CardTitle>
                    <CardDescription>Manage blocked IPs and block durations</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search IP address..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-xs"
                    />
                    <Button onClick={() => setManualBlockDialogOpen(true)}>
                      <Ban className="h-4 w-4 mr-2" />
                      Manual Block
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredBlockedIPs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No blocked IPs found
                  </p>
                ) : (
                  <div className="space-y-3">
                    {filteredBlockedIPs.map((ip) => (
                      <div
                        key={ip.id}
                        className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
                      >
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-mono font-medium">{ip.ip_address}</p>
                            {ip.is_manual ? (
                              <Badge variant="outline">Manual</Badge>
                            ) : (
                              <Badge variant="secondary">Automatic</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-muted-foreground">
                            <span>Blocked: {format(new Date(ip.blocked_at), "MMM d, h:mm a")}</span>
                            <span>Unblocks: {format(new Date(ip.unblock_at), "MMM d, h:mm a")}</span>
                            <span>Violations: {ip.violation_count}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{ip.block_reason}</p>
                          {ip.notes && <p className="text-xs text-muted-foreground italic">Notes: {ip.notes}</p>}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedBlockedIP(ip);
                            setUnblockDialogOpen(true);
                          }}
                        >
                          <Unlock className="h-4 w-4 mr-1" />
                          Unblock
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Adjust Reputation Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust IP Reputation</DialogTitle>
            <DialogDescription>
              Manually adjust the reputation score for {selectedIP?.ip_address}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reputation">New Reputation Score</Label>
              <Input
                id="reputation"
                type="number"
                value={newReputation}
                onChange={(e) => setNewReputation(parseInt(e.target.value))}
                min={-1000}
                max={200}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Current: {selectedIP?.reputation_score} | Range: -1000 to 200
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdjustReputation}>
              Update Reputation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unblock IP Dialog */}
      <AlertDialog open={unblockDialogOpen} onOpenChange={setUnblockDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock IP Address</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unblock {selectedBlockedIP?.ip_address}? This will immediately restore access for this IP address.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnblockIP}>
              Unblock IP
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual Block Dialog */}
      <Dialog open={manualBlockDialogOpen} onOpenChange={setManualBlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manually Block IP Address</DialogTitle>
            <DialogDescription>
              Block an IP address for 30 days with a custom reason
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="blockIP">IP Address</Label>
              <Input
                id="blockIP"
                placeholder="192.168.1.1"
                value={manualBlockIP}
                onChange={(e) => setManualBlockIP(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="blockReason">Block Reason</Label>
              <Input
                id="blockReason"
                placeholder="e.g., Suspicious activity detected"
                value={manualBlockReason}
                onChange={(e) => setManualBlockReason(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="blockNotes">Notes (Optional)</Label>
              <Textarea
                id="blockNotes"
                placeholder="Additional context or notes..."
                value={manualBlockNotes}
                onChange={(e) => setManualBlockNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualBlockDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleManualBlock} variant="destructive">
              Block IP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIPManagement;
